import React, { useEffect, useState } from 'react';
import Layout from './Layout';
import Logo from '../logo/Rccglogo.png';
import { useTheme } from '../context/ThemeContext';
import Swal from 'sweetalert2';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from "jspdf-autotable"; // ✅ Import correctly

const Reports = () => {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('visitors');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [visitors, setVisitors] = useState({
    data: [],
    pagination: { page: 1, pages: 0, total: 0 },
  });
  const [students, setStudents] = useState({
    data: [],
    pagination: { page: 1, pages: 0, total: 0 },
  });
  const [newBirths, setNewBirths] = useState({
    data: [],
    pagination: { page: 1, pages: 0, total: 0 },
  });
  const [studentStats, setStudentStats] = useState([]);
  const [smsStats, setSmsStats] = useState(null);
  const [locations, setLocations] = useState([]);

  const [visitorFilters, setVisitorFilters] = useState({
    startDate: '',
    endDate: '',
    search: '',
    visitor_type: '',
    service_attended: '',
    status: '',
    location_id: '',
    age_group: '',
  });

  const [studentFilters, setStudentFilters] = useState({
    startDate: '',
    endDate: '',
    search: '',
    student_status: '',
    school: '',
    pickup_location: '',
  });

  const [newBirthFilters, setNewBirthFilters] = useState({
    startDate: '',
    endDate: '',
    decision: '',
    residency_status: '',
    service_attended: '',
  });

  const [studentStatsFilters, setStudentStatsFilters] = useState({
    startDate: '',
    endDate: '',
    search: '',
    student_status: '',
    school: '',
    pickup_location: '',
  });

  // Device-aware API base
  const deriveApiBase = () => {
    // Force production URL when deployed
    if (window.location.hostname === 'rccgtr.net' || window.location.hostname.includes('rccgtr')) {
      return 'https://rccgtr.net/api';
    }

    // Development only
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    }

    // Fallback to relative path
    return '/api';
  };
  const API_BASE_URL = deriveApiBase();

  // School -> pickup options
  const pickupLocations = {
    'University of Abuja': [
      { value: '', label: 'All' },
      { value: 'boys_hostel_mini_campus', label: 'Boys Hostel (Mini Campus)' },
      { value: 'boys_hostel_main_campus', label: 'Boys Hostel (Main Campus)' },
      { value: 'girls_hostel_main_campus', label: 'Girls Hostel (Main Campus)' },
      { value: 'permanent_site_gate', label: 'Permanent Site Gate' },
      { value: 'ido_junction', label: 'Ido Junction' },
      { value: 'saoka_bridge_immigration', label: 'Saoka Bridge (Immigration)' },
      { value: 'lugbe_car_wash', label: 'Lugbe by Car Wash' },
      { value: 'lugbe_police_sign_post', label: 'Lugbe by Police Sign Post' },
    ],
    'Nasarawa State University': [
      { value: '', label: 'All' },
      { value: 'school_gate', label: 'School Gate' },
      { value: 'keffi_bridge', label: 'Keffi Bridge' },
      { value: 'maraba_junction', label: 'Maraba Junction' },
      { value: 'nyanya_bridge', label: 'Nyanya Bridge' },
    ],
  };

  const getPickupLabel = value => {
    for (const arr of Object.values(pickupLocations)) {
      const found = arr.find(x => x.value === value);
      if (found) return found.label;
    }
    return value;
  };

  // Load SMS Stats (always shown at top)
  const loadSmsStats = async () => {
    try {
      const { data: smsData } = await axios.get(`${API_BASE_URL}/sms/stats`);
      if (smsData.success) {
        setSmsStats(smsData.data);
      }
    } catch (err) {
      console.error('Error loading SMS stats:', err);
    }
  };

  // Load Visitors
  const loadVisitors = async () => {
    try {
      setLoading(true);
      setError('');

      const vParams = new URLSearchParams({
        page: visitors.pagination.page,
        limit: 20,
        visitor_type: visitorFilters.visitor_type,
        service_attended: visitorFilters.service_attended,
        status: visitorFilters.status,
        search: visitorFilters.search,
        startDate: visitorFilters.startDate,
        endDate: visitorFilters.endDate,
        location_id: visitorFilters.location_id,
        age_group: visitorFilters.age_group,
      });

      const { data: vData } = await axios.get(`${API_BASE_URL}/visitors`, { params: Object.fromEntries(vParams) });

      if (vData.success) {
        setVisitors({ data: vData.data, pagination: vData.pagination });
      } else {
        setError('Failed to load visitors');
      }
    } catch (err) {
      console.error('Error loading visitors:', err);
      setError('Error loading visitors');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllFilteredVisitors = async () => {
    const res = await axios.get(`${API_BASE_URL}/visitors`, {
      params: {
        all: true,
        startDate: visitorFilters.startDate,
        endDate: visitorFilters.endDate,
        search: visitorFilters.search,
        visitor_type: visitorFilters.visitor_type,
        service_attended: visitorFilters.service_attended,
        status: visitorFilters.status,
        location_id: visitorFilters.location_id,
        age_group: visitorFilters.age_group,
      },
    });

    return res.data.data;
  };

  const handleExportVisitors = async () => {
    try {
      const rawRows = await fetchAllFilteredVisitors();

      const formattedRows = rawRows.map((v) => ({
        Name: `${v.first_name || ''} ${v.last_name || ''}`.trim(),
        Phone: v.phone || '',
        Email: v.email || '',
        Type: v.visitor_type ? v.visitor_type.replace('_', ' ') : '',
        Service: v.service_attended ? v.service_attended.replace('_', ' ') : '',
        Location: v.location_name || 'Others',
        City: v.other_city || 'Abuja',
        Status: v.status || 'Pending',
        Date: v.created_at ? new Date(v.created_at).toLocaleDateString() : '',
      }));

      exportCSV(formattedRows, "visitors_report.csv", "Visitors Report (Grouped by Location)", visitorFilters, "Location");
    } catch (error) {
      console.error("Visitors Export Error:", error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: 'An error occurred while exporting visitors data.',
      });
    }
  };

  const handleExportVisitorsPDF = async () => {
    try {
      const rawRows = await fetchAllFilteredVisitors();
      const formattedRows = rawRows.map((v) => ({
        Name: `${v.first_name || ''} ${v.last_name || ''}`.trim(),
        Phone: v.phone || '',
        Email: v.email || '',
        Type: v.visitor_type ? v.visitor_type.replace('_', ' ') : '',
        Service: v.service_attended ? v.service_attended.replace('_', ' ') : '',
        Location: v.location_name || 'Others',
        City: v.other_city || 'Abuja',
        Status: v.status || 'Pending',
        Date: v.created_at ? new Date(v.created_at).toLocaleDateString() : '',
      }));
      exportPDF(formattedRows, 'visitors_report.pdf', "Visitors Report (Grouped by Location)", visitorFilters, "Location");
    } catch (error) {
      console.error("Visitors PDF Export Error:", error);
    }
  };


  // Load New Births
  const loadNewBirths = async () => {
    try {
      setLoading(true);
      setError('');

      const nbParams = new URLSearchParams({
        page: newBirths.pagination.page,
        limit: 20,
        startDate: newBirthFilters.startDate,
        endDate: newBirthFilters.endDate,
        decision: newBirthFilters.decision,
        residency_status: newBirthFilters.residency_status,
        service_attended: newBirthFilters.service_attended,
      });

      const { data: nbData } = await axios.get(`${API_BASE_URL}/new-birth-cards`, { params: Object.fromEntries(nbParams) });

      if (nbData.success) {
        setNewBirths({ data: nbData.data, pagination: nbData.pagination });
      } else {
        setError('Failed to load new birth records');
      }
    } catch (err) {
      console.error('Error loading new births:', err);
      setError('Error loading new birth records');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllFilteredNewBirths = async () => {
    const res = await axios.get(`${API_BASE_URL}/new-birth-cards`, {
      params: {
        all: true,
        startDate: newBirthFilters.startDate,
        endDate: newBirthFilters.endDate,
        decision: newBirthFilters.decision,
        residency_status: newBirthFilters.residency_status,
        service_attended: newBirthFilters.service_attended,
      },
    });

    return res.data.data;
  };

  const handleExportNewBirths = async () => {
    try {
      const rawRows = await fetchAllFilteredNewBirths();

      const formattedRows = rawRows.map((n) => ({
        Date: n.response_date ? new Date(n.response_date).toLocaleDateString() : '',
        Decision: n.decision ? n.decision.replace('_', ' ') : '',
        Name: `${n.first_name || ''} ${n.last_name || ''}`.trim(),
        Phone: n.phone_main || '',
        Email: n.email || '',
        Gender: n.gender || '',
        Service: n.service_attended ? n.service_attended.replace('_', ' ') : '',
        Residency: n.residency_status || '',
      }));

      exportCSV(formattedRows, "newbirths_report.csv", "New Birth Report", newBirthFilters);
    } catch (error) {
      console.error("NewBirth Export Error:", error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: 'An error occurred while exporting new birth records.',
      });
    }
  };

  const handleExportNewBirthsPDF = async () => {
    try {
      const rawRows = await fetchAllFilteredNewBirths();
      const formattedRows = rawRows.map((n) => ({
        Date: n.response_date ? new Date(n.response_date).toLocaleDateString() : '',
        Decision: n.decision ? n.decision.replace('_', ' ') : '',
        Name: `${n.first_name || ''} ${n.last_name || ''}`.trim(),
        Phone: n.phone_main || '',
        Email: n.email || '',
        Gender: n.gender || '',
        Service: n.service_attended ? n.service_attended.replace('_', ' ') : '',
        Residency: n.residency_status || '',
      }));
      exportPDF(formattedRows, "newbirths_report.pdf", "New Birth Report (Grouped by Residency)", newBirthFilters, "Residency");
    } catch (error) {
      console.error("NewBirth PDF Export Error:", error);
    }
  };

  // Load Students
  const loadStudents = async () => {
    try {
      setLoading(true);
      setError('');

      const sParams = new URLSearchParams({
        page: students.pagination.page,
        limit: 20,
        status: studentFilters.student_status,
        school: studentFilters.school,
        pickup_location: studentFilters.pickup_location,
        search: studentFilters.search,
        startDate: studentFilters.startDate,
        endDate: studentFilters.endDate,
      });

      const { data: sData } = await axios.get(`${API_BASE_URL}/students`, { params: Object.fromEntries(sParams) });

      if (sData.success) {
        setStudents({ data: sData.data, pagination: sData.pagination });
      } else {
        setError('Failed to load students');
      }
    } catch (err) {
      console.error('Error loading students:', err);
      setError('Error loading students');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllFilteredStudents = async () => {
    const res = await axios.get(`${API_BASE_URL}/students`, {
      params: {
        all: true,
        status: studentFilters.student_status,
        school: studentFilters.school,
        pickup_location: studentFilters.pickup_location,
        search: studentFilters.search,
        startDate: studentFilters.startDate,
        endDate: studentFilters.endDate,
      },
    });

    return res.data.data;
  };

  const handleExportStudents = async () => {
    try {
      const rawRows = await fetchAllFilteredStudents();

      const formattedRows = rawRows.map((s) => ({
        Name: s.full_name,
        Phone: s.phone || '',
        Email: s.email || '',
        School: s.school,
        Level: s.school_level,
        PickupLocation: getPickupLabel(s.pickup_location),
        Invitee: s.number_of_people,
        Status: s.status || 'Pending',
        Date: s.created_at ? new Date(s.created_at).toLocaleDateString() : '',
      }));

      exportCSV(formattedRows, "students_report.csv", "Students Report", studentFilters);
    } catch (error) {
      console.error("Student Export Error:", error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: 'An error occurred while exporting students data.',
      });
    }
  };

  const handleExportStudentsPDF = async () => {
    try {
      const rawRows = await fetchAllFilteredStudents();
      const formattedRows = rawRows.map((s) => ({
        Name: s.full_name,
        Phone: s.phone || '',
        Email: s.email || '',
        School: s.school,
        Level: s.school_level,
        PickupLocation: getPickupLabel(s.pickup_location),
        Invitee: s.number_of_people,
        Status: s.status || 'Pending',
        Date: s.created_at ? new Date(s.created_at).toLocaleDateString() : '',
      }));
      exportPDF(formattedRows, "students_report.pdf", "Students Report (Grouped by School)", studentFilters, "School");
    } catch (error) {
      console.error("Student PDF Export Error:", error);
    }
  };

  // Load Student Stats
  const loadStudentStats = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        status: studentStatsFilters.student_status,
        school: studentStatsFilters.school,
        pickup_location: studentStatsFilters.pickup_location,
        search: studentStatsFilters.search,
        startDate: studentStatsFilters.startDate,
        endDate: studentStatsFilters.endDate,
      });

      const { data } = await axios.get(`${API_BASE_URL}/students/stats-grouped`, { params: Object.fromEntries(params) });

      if (data.success) {
        setStudentStats(data.data);
      } else {
        setError('Failed to load student statistics');
      }
    } catch (err) {
      console.error('Error loading student stats:', err);
      setError('Error loading student statistics');
    } finally {
      setLoading(false);
    }
  };

  // Load locations
  const loadLocations = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/locations`, { params: { active_only: true } });
      if (data.success) {
        setLocations(data.data);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  // Initial load
  useEffect(() => {
    loadSmsStats();
    loadLocations();
  }, []);

  // Load data when filters change
  useEffect(() => {
    if (activeTab === 'visitors') {
      loadVisitors();
    }
  }, [visitorFilters, visitors.pagination.page, activeTab]);

  useEffect(() => {
    if (activeTab === 'students') {
      loadStudents();
    }
  }, [studentFilters, students.pagination.page, activeTab]);

  useEffect(() => {
    if (activeTab === 'student-stats') {
      loadStudentStats();
    }
  }, [studentStatsFilters, activeTab]);

  useEffect(() => {
    if (activeTab === 'newbirth') {
      loadNewBirths();
    }
  }, [newBirthFilters, newBirths.pagination.page, activeTab]);

  // Update visitor filters
  const updateVisitorFilter = (key, value) => {
    setVisitorFilters(prev => ({ ...prev, [key]: value }));
    setVisitors(v => ({ ...v, pagination: { ...v.pagination, page: 1 } }));
  };

  // Update student filters
  const updateStudentFilter = (key, value) => {
    setStudentFilters(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'school') {
        next.pickup_location = value ? pickupLocations[value]?.[0]?.value || '' : '';
      }
      return next;
    });
    setStudents(s => ({ ...s, pagination: { ...s.pagination, page: 1 } }));
  };

  // Update student stats filters
  const updateStudentStatsFilter = (key, value) => {
    setStudentStatsFilters(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'school') {
        next.pickup_location = value ? pickupLocations[value]?.[0]?.value || '' : '';
      }
      return next;
    });
  };

  // Export CSV function
  // const exportCSV = (rows, filename) => {
  //   try {
  //     if (!rows || rows.length === 0) {
  //       Swal.fire({
  //         icon: 'warning',
  //         title: 'No Data',
  //         text: 'No data available to export',
  //       });
  //       return;
  //     }

  //     const headers = Object.keys(rows[0]);
  //     const csv = [headers.join(',')]
  //       .concat(rows.map(r => headers.map(h => JSON.stringify(r[h] ?? '')).join(',')))
  //       .join('\n');

  //     const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  //     const url = URL.createObjectURL(blob);
  //     const link = document.createElement('a');
  //     link.href = url;
  //     link.download = filename;
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //     URL.revokeObjectURL(url);

  //     Swal.fire({
  //       icon: 'success',
  //       title: 'Export Successful',
  //       text: `${filename} has been downloaded successfully!`,
  //       timer: 2000,
  //       showConfirmButton: false,
  //     });
  //   } catch (e) {
  //     Swal.fire({
  //       icon: 'error',
  //       title: 'Export Failed',
  //       text: 'Failed to export CSV file',
  //     });
  //   }
  // };

  const exportCSV = (rows, filename, reportTitle = "Report", filters = {}, groupBy = null) => {
    if (!rows || rows.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Data",
        text: "No data available to export",
      });
      return;
    }

    const csvRows = [];

    // --- TITLE ROWS ---
    csvRows.push(`${reportTitle}`);
    csvRows.push(`Generated On:, ${new Date().toLocaleDateString()}`);

    if (filters.startDate || filters.endDate) {
      csvRows.push(`Date Range:, ${filters.startDate || "All"} → ${filters.endDate || "All"}`);
    }

    csvRows.push(""); // empty spacer row

    if (groupBy && rows[0][groupBy] !== undefined) {
      // --- GROUPED BODY ---
      const grouped = rows.reduce((acc, row) => {
        const key = row[groupBy] || 'Others';
        if (!acc[key]) acc[key] = [];
        acc[key].push(row);
        return acc;
      }, {});

      const sortedKeys = Object.keys(grouped).sort();
      const headerKeys = Object.keys(rows[0]);
      const header = ["S/N", ...headerKeys];

      sortedKeys.forEach(key => {
        csvRows.push(`"${groupBy.toUpperCase()}: ${key.toString().toUpperCase()}"`);
        csvRows.push(header.join(","));

        grouped[key].forEach((row, index) => {
          const values = [
            index + 1,
            ...headerKeys.map(k => {
              let v = row[k];
              if (v === null || v === undefined) v = "";
              // Escape quotes and wrap in quotes
              return `"${v.toString().replace(/"/g, '""')}"`;
            })
          ];
          csvRows.push(values.join(","));
        });
        csvRows.push(""); // spacer row between groups
      });
    } else {
      // --- FLAT BODY ---
      const headerKeys = Object.keys(rows[0]);
      const header = ["S/N", ...headerKeys];
      csvRows.push(header.join(","));

      rows.forEach((row, index) => {
        const sn = index + 1;
        const values = [
          sn,
          ...headerKeys.map((key) => {
            let v = row[key];
            if (v === null || v === undefined) v = "";
            return `"${v.toString().replace(/"/g, '""')}"`;
          }),
        ];
        csvRows.push(values.join(","));
      });
    }


    // Convert to blob
    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });

    // Download
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();

    Swal.fire({
      icon: "success",
      title: "Export Successful",
      text: `${filename} has been downloaded successfully!`,
      timer: 2000,
      showConfirmButton: false,
    });
  };


  const exportPDF = (rows, filename, reportTitle = "Report", reportFilters = {}, groupBy = null) => {
    try {
      if (!rows || rows.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "No Data",
          text: "No data available to export",
        });
        return;
      }

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "A4",
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // ✅ Add your logo
      const logoUrl = Logo;
      doc.addImage(logoUrl, "PNG", 40, 20, 80, 50);

      const today = new Date().toLocaleDateString();

      doc.setFontSize(18);
      doc.text(reportTitle, pageWidth / 2, 60, { align: "center" });

      let yPosition = 85;
      if (reportFilters?.startDate || reportFilters?.endDate) {
        const start = reportFilters.startDate || "All";
        const end = reportFilters.endDate || "All";
        doc.setFontSize(9);
        doc.text(`Date Range: ${start} - ${end}`, pageWidth / 2, yPosition, { align: "center" });
        yPosition += 15;
      }

      doc.setFontSize(11);
      doc.text(`Generated on: ${today}`, pageWidth - 150, 40);

      const headers = ["S/N", ...Object.keys(rows[0])];
      const headerKeys = Object.keys(rows[0]);

      if (groupBy && rows[0][groupBy] !== undefined) {
        // --- GROUPED PDF ---
        const grouped = rows.reduce((acc, row) => {
          const key = row[groupBy] || 'Others';
          if (!acc[key]) acc[key] = [];
          acc[key].push(row);
          return acc;
        }, {});

        const sortedKeys = Object.keys(grouped).sort();
        let currentY = yPosition + 10;

        sortedKeys.forEach((key, groupIdx) => {
          // Add Group Title
          doc.setFontSize(12);
          doc.setFont("helvetica", "bold");
          const groupTitle = `${groupBy.toUpperCase()}: ${key.toString().toUpperCase()}`;

          // Check if we need a new page for the group title + at least one row
          if (currentY > pageHeight - 100) {
            doc.addPage();
            currentY = 60; // Start higher on new page
          }

          doc.text(groupTitle, 40, currentY);
          currentY += 10;

          const groupData = grouped[key].map((r, i) => [
            i + 1,
            ...headerKeys.map(h => r[h] ?? "")
          ]);

          autoTable(doc, {
            head: [headers],
            body: groupData,
            startY: currentY,
            theme: "striped",
            headStyles: { fillColor: [41, 128, 185], textColor: 255, halign: "center" },
            styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
            margin: { top: 60, bottom: 40 },
            didDrawPage: (data) => {
              // Footer
              const pageCount = doc.internal.getNumberOfPages();
              doc.setFontSize(10);
              doc.setTextColor("#555");
              doc.text(`Page ${data.pageNumber}`, pageWidth / 2, pageHeight - 20, { align: "center" });
            },
          });

          currentY = doc.lastAutoTable.finalY + 30;
        });
      } else {
        // --- FLAT PDF ---
        const data = rows.map((r, i) => [i + 1, ...headerKeys.map(h => r[h] ?? "")]);

        autoTable(doc, {
          head: [headers],
          body: data,
          startY: 100,
          theme: "striped",
          headStyles: { fillColor: [41, 128, 185], textColor: 255, halign: "center" },
          styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
          didDrawPage: (data) => {
            const pageCount = doc.internal.getNumberOfPages();
            doc.setFontSize(10);
            doc.setTextColor("#555");
            doc.text(`Page ${data.pageNumber}`, pageWidth / 2, pageHeight - 20, { align: "center" });
          },
        });
      }

      doc.save(filename);
      Swal.fire({
        icon: "success",
        title: "Export Successful",
        text: `${filename} has been downloaded successfully!`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error exporting PDF:", error);
      Swal.fire({ icon: "error", title: "Export Failed", text: "Failed to export PDF file" });
    }
  };

  // Converts visible visitors table rows to clean exportable format
  const getDisplayedVisitors = () => {
    return visitors.data.map((v, i) => ({
      SN: i + 1,
      Name: `${v.first_name || ''} ${v.last_name || ''}`.trim(),
      Phone: v.phone || '',
      Email: v.email || '',
      Type: v.visitor_type ? v.visitor_type.replace('_', ' ') : '',
      Service: v.service_attended ? v.service_attended.replace('_', ' ') : '',
      Location: v.location_name || 'Others',
      City: v.other_city || 'Abuja',
      Status: v.status || 'Pending',
      Date: v.created_at ? new Date(v.created_at).toLocaleDateString() : '',
    }));
  };

  // Converts visible Student table rows to clean exportable format
  const getDisplayedStudents = () => {
    return students.data.map((s, i) => ({
      SN: i + 1,
      Name: s.full_name,
      Phone: s.phone || '',
      Email: s.email || '',
      School: s.school,
      Level: s.school_level,
      PickupLocation: getPickupLabel(s.pickup_location),
      Invitee: s.number_of_people,
      Status: s.status || 'Pending',
      Date: s.created_at ? new Date(s.created_at).toLocaleDateString() : '',
    }));
  };


  // Converts visible NewBirth table rows to clean exportable format
  const getDisplayedNewBirth = () => {
    return newBirths.data.map((n, i) => ({
      SN: i + 1,
      Date: n.response_date ? new Date(n.response_date).toLocaleDateString() : '',
      Decision: n.decision ? n.decision.replace('_', ' ') : '',
      Name: `${n.first_name || ''} ${n.last_name || ''}`.trim(),
      Phone: n.phone_main,
      Email: n.email,
      Gender: n.gender,
      Service: n.service_attended ? n.service_attended.replace('_', ' ') : '',
      Residency: n.residency_status,

    }));
  };


  // Converts visible Student table rows to clean exportable format
  const getDisplayedStudentStats = () => {
    return studentStats.data.map((stat, i) => ({
      SN: i + 1,
      Name: stat.full_name,
      Status: stat.status || 'Pending',
      School: stat.school || 'All Schools',
      StudentCount: stat.student_count || '',
      Percentage: `${stat.percentage || 0}%`,
      TotalInvitees: stat.total_invitees,
    }));
  };

  const renderPagination = (pagination, setPageFn) => {
    if (!pagination.pages || pagination.pages <= 1) return null;

    return (
      <div className="flex justify-center items-center gap-2 mt-4">
        <button
          onClick={() => setPageFn(pagination.page - 1)}
          disabled={pagination.page === 1}
          className="px-3 py-1 bg-indigo-600 text-white rounded disabled:opacity-50"
        >
          Previous
        </button>

        <span className="px-3 py-1">
          Page {pagination.page} of {pagination.pages}
        </span>

        <button
          onClick={() => setPageFn(pagination.page + 1)}
          disabled={pagination.page === pagination.pages}
          className="px-3 py-1 bg-indigo-600 text-white rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    );
  };


  return (
    <Layout title="Reports">
      <div className="max-w-6xl mx-auto mt-6 space-y-6">
        {/* SMS Summary - Always at the top */}
        <div
          className={`${isDark ? 'bg-gray-800 text-indigo-100' : 'bg-white'} p-6 rounded-xl shadow`}
        >
          <h3 className="font-bold text-lg mb-4">SMS Summary</h3>
          {!smsStats ? (
            <p className="text-sm opacity-70">Loading SMS data...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <p className="text-sm opacity-80 mb-2">Total Scheduled</p>
                <p className="text-2xl font-bold text-blue-600">{smsStats.total || 0}</p>
              </div>
              <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <p className="text-sm opacity-80 mb-2">Status Breakdown</p>
                <div className="space-y-1">
                  {smsStats.statusBreakdown?.map((row, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="capitalize">{row.status}</span>
                      <span className="font-semibold">{row.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <p className="text-sm opacity-80 mb-2">Category Breakdown</p>
                <div className="space-y-1">
                  {smsStats.categoryBreakdown?.map((row, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="capitalize">{row.category}</span>
                      <span className="font-semibold">{row.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow overflow-hidden`}>
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('visitors')}
              className={`px-6 py-4 font-medium transition-colors ${activeTab === 'visitors'
                ? `${isDark
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                }`
                : `${isDark
                  ? 'text-gray-300 hover:text-white'
                  : 'text-gray-500 hover:text-gray-700'
                }`
                }`}
            >
              Visitors ({visitors.pagination.total || 0})
            </button>
            <button
              onClick={() => setActiveTab('students')}
              className={`px-6 py-4 font-medium transition-colors ${activeTab === 'students'
                ? `${isDark
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                }`
                : `${isDark
                  ? 'text-gray-300 hover:text-white'
                  : 'text-gray-500 hover:text-gray-700'
                }`
                }`}
            >
              Students ({students.pagination.total || 0})
            </button>

            <button
              onClick={() => setActiveTab('newbirth')}
              className={`px-6 py-4 font-medium transition-colors ${activeTab === 'newbirth'
                ? `${isDark
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                }`
                : `${isDark
                  ? 'text-gray-300 hover:text-white'
                  : 'text-gray-500 hover:text-gray-700'
                }`
                }`}
            >
              New Birth ({newBirths.pagination.total || 0})
            </button>

            <button
              onClick={() => setActiveTab('student-stats')}
              className={`px-6 py-4 font-medium transition-colors ${activeTab === 'student-stats'
                ? `${isDark
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                }`
                : `${isDark
                  ? 'text-gray-300 hover:text-white'
                  : 'text-gray-500 hover:text-gray-700'
                }`
                }`}
            >
              Student Stats
            </button>
          </div>

          <div className="p-6">
            {/* Visitors Tab */}
            {activeTab === 'visitors' && (
              <div className="space-y-6">
                {/* Visitor Filters */}
                <div className="space-y-4">
                  <h4 className="font-semibold">Filters</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Date Range */}
                    <div className="col-span-1 md:col-span-2 lg:col-span-1">
                      <label className="text-sm font-medium block mb-2">Date Range</label>
                      <div className="flex gap-2">
                        <input
                          type="date"
                          value={visitorFilters.startDate}
                          onChange={e => updateVisitorFilter('startDate', e.target.value)}
                          className={`border rounded px-3 py-2 flex-1 ${isDark
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300'
                            }`}
                        />
                        <input
                          type="date"
                          value={visitorFilters.endDate}
                          onChange={e => updateVisitorFilter('endDate', e.target.value)}
                          className={`border rounded px-3 py-2 flex-1 ${isDark
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300'
                            }`}
                        />
                      </div>
                    </div>

                    {/* Search */}
                    <div>
                      <label className="text-sm font-medium block mb-2">Search</label>
                      <input
                        value={visitorFilters.search}
                        onChange={e => updateVisitorFilter('search', e.target.value)}
                        placeholder="Name, phone, email..."
                        className={`border rounded px-3 py-2 w-full ${isDark
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300'
                          }`}
                      />
                    </div>

                    {/* Visitor Type */}
                    <div>
                      <label className="text-sm font-medium block mb-2">Visitor Type</label>
                      <select
                        value={visitorFilters.visitor_type}
                        onChange={e => updateVisitorFilter('visitor_type', e.target.value)}
                        className={`border rounded px-3 py-2 w-full ${isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300'
                          }`}
                      >
                        <option value="">All</option>
                        <option value="first_timer">First Timer</option>
                        <option value="returning_member">Returning Member</option>
                      </select>
                    </div>

                    {/* Service Attended */}
                    <div>
                      <label className="text-sm font-medium block mb-2">Service Attended</label>
                      <select
                        value={visitorFilters.service_attended}
                        onChange={e => updateVisitorFilter('service_attended', e.target.value)}
                        className={`border rounded px-3 py-2 w-full ${isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300'
                          }`}
                      >
                        <option value="">All</option>
                        <option value="first_service">First Service</option>
                        <option value="second_service">Second Service</option>
                        <option value="expression_church">Expression Church</option>
                        <option value="midweek">Midweek</option>
                      </select>
                    </div>

                    {/* Visitor Status */}
                    <div>
                      <label className="text-sm font-medium block mb-2">Status</label>
                      <select
                        value={visitorFilters.status}
                        onChange={e => updateVisitorFilter('status', e.target.value)}
                        className={`border rounded px-3 py-2 w-full ${isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300'
                          }`}
                      >
                        <option value="">All</option>
                        <option value="new">New Member</option>
                        <option value="return">Returning Member</option>
                      </select>
                    </div>

                    {/* Location Filter */}
                    <div>
                      <label className="text-sm font-medium block mb-2">Location</label>
                      <select
                        value={visitorFilters.location_id}
                        onChange={e => updateVisitorFilter('location_id', e.target.value)}
                        className={`border rounded px-3 py-2 w-full ${isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300'
                          }`}
                      >
                        <option value="">All Locations</option>
                        {locations.map(location => (
                          <option key={location.id} value={location.id}>
                            {location.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Age Group Filter */}
                    <div>
                      <label className="text-sm font-medium block mb-2">Age Group</label>
                      <select
                        value={visitorFilters.age_group}
                        onChange={e => updateVisitorFilter('age_group', e.target.value)}
                        className={`border rounded px-3 py-2 w-full ${isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300'
                          }`}
                      >
                        <option value="">All</option>
                        <option value="13-19">13-19</option>
                        <option value="20-24">20-24</option>
                        <option value="25-29">25-29</option>
                        <option value="30-34">30-34</option>
                        <option value="35-39">35-39</option>
                        <option value="40-44">40-44</option>
                        <option value="45-49">45-49</option>
                        <option value="50-54">50-54</option>
                        <option value="55-59">55-59</option>
                        <option value="60+">60+</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Export Button */}
                <div className="flex justify-end gap-2">
                  <button
                    // onClick={() => exportCSV(fetchAllFilteredVisitors(), 'visitors_report.csv', "Visitors Report", visitorFilters)}
                    onClick={handleExportVisitors}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                  >
                    Export CSV
                  </button>

                  <button
                    onClick={handleExportVisitorsPDF}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Export PDF
                  </button>
                </div>

                {/* Visitors Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">SN</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Phone</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Email</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Service</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Location</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">city</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        {/* <th className="px-4 py-3 text-left text-sm font-medium">Date</th> */}
                      </tr>
                    </thead>
                    <tbody className={`${isDark ? 'divide-gray-700' : 'divide-gray-200'} divide-y`}>
                      {loading ? (
                        <tr>
                          <td className="px-4 py-8 text-center" colSpan={9}>
                            <div className="flex justify-center items-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                              <span className="ml-2">Loading visitors...</span>
                            </div>
                          </td>
                        </tr>
                      ) : visitors.data.length === 0 ? (
                        <tr>
                          <td className="px-4 py-8 text-center text-gray-500" colSpan={9}>
                            No visitors found
                          </td>
                        </tr>
                      ) : (
                        visitors.data.map((v, i) => (
                          <tr key={v.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">{i + 1}</td>
                            <td className="px-4 py-3 text-sm">
                              {v.first_name} {v.last_name}
                            </td>
                            <td className="px-4 py-3 text-sm">{v.phone}</td>
                            <td className="px-4 py-3 text-sm">{v.email}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className="capitalize">
                                {v.visitor_type?.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className="capitalize">
                                {v.service_attended?.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {v.location_name || 'others'}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {v.other_city || 'Abuja'}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${v.status === 'converted'
                                  ? 'bg-green-100 text-green-800'
                                  : v.status === 'contacted'
                                    ? 'bg-blue-100 text-blue-800'
                                    : v.status === 'cancelled'
                                      ? 'bg-red-100 text-red-800'

                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}
                              >
                                {v.status}
                              </span>
                            </td>
                            {/* <td className="px-4 py-3 text-sm">
                              {new Date(v.created_at).toLocaleDateString()}
                            </td> */}
                          </tr>
                        ))

                      )}

                    </tbody>
                  </table>
                  {renderPagination(visitors.pagination, (p) =>
                    setVisitors(prev => ({ ...prev, pagination: { ...prev.pagination, page: p } }))
                  )}

                </div>
              </div>
            )}

            {/* Students Tab */}
            {activeTab === 'students' && (
              <div className="space-y-6">
                {/* Student Filters */}
                <div className="space-y-4">
                  <h4 className="font-semibold">Filters</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Date Range */}
                    <div className="col-span-1 md:col-span-2 lg:col-span-1">
                      <label className="text-sm font-medium block mb-2">Date Range</label>
                      <div className="flex gap-2">
                        <input
                          type="date"
                          value={studentFilters.startDate}
                          onChange={e => updateStudentFilter('startDate', e.target.value)}
                          className={`border rounded px-3 py-2 flex-1 ${isDark
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300'
                            }`}
                        />
                        <input
                          type="date"
                          value={studentFilters.endDate}
                          onChange={e => updateStudentFilter('endDate', e.target.value)}
                          className={`border rounded px-3 py-2 flex-1 ${isDark
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300'
                            }`}
                        />
                      </div>
                    </div>

                    {/* Search */}
                    <div>
                      <label className="text-sm font-medium block mb-2">Search</label>
                      <input
                        value={studentFilters.search}
                        onChange={e => updateStudentFilter('search', e.target.value)}
                        placeholder="Name, phone, email..."
                        className={`border rounded px-3 py-2 w-full ${isDark
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300'
                          }`}
                      />
                    </div>

                    {/* Student Status */}
                    <div>
                      <label className="text-sm font-medium block mb-2">Status</label>
                      <select
                        value={studentFilters.student_status}
                        onChange={e => updateStudentFilter('student_status', e.target.value)}
                        className={`border rounded px-3 py-2 w-full ${isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300'
                          }`}
                      >
                        <option value="">All</option>
                        <option value="pending">Pending</option>
                        <option value="contacted">Contacted</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>

                    {/* School */}
                    <div>
                      <label className="text-sm font-medium block mb-2">School</label>
                      <select
                        value={studentFilters.school}
                        onChange={e => updateStudentFilter('school', e.target.value)}
                        className={`border rounded px-3 py-2 w-full ${isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300'
                          }`}
                      >
                        <option value="">All</option>
                        <option value="University of Abuja">University of Abuja</option>
                        <option value="Nasarawa State University">Nasarawa State University</option>
                      </select>
                    </div>

                    {/* Pickup Location */}
                    <div>
                      <label className="text-sm font-medium block mb-2">Pickup Location</label>
                      <select
                        value={studentFilters.pickup_location}
                        onChange={e => updateStudentFilter('pickup_location', e.target.value)}
                        className={`border rounded px-3 py-2 w-full ${isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300'
                          }`}
                        disabled={!studentFilters.school}
                      >
                        {/* <option value="">All</option> */}
                        {studentFilters.school &&
                          pickupLocations[studentFilters.school]?.map(loc => (
                            <option key={loc.value} value={loc.value}>
                              {loc.label}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Export Button */}
                <div className="flex justify-end gap-2">
                  <button
                    // onClick={() => exportCSV(getDisplayedStudents(), 'students_report.csv', visitorFilters)}
                    onClick={handleExportStudents}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                  >
                    Export CSV
                  </button>

                  <button
                    onClick={handleExportStudentsPDF}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Export PDF
                  </button>
                </div>

                {/* Students Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">SN</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Phone</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Email</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">School</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Level</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Pickup Location</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Invitee</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody className={`${isDark ? 'divide-gray-700' : 'divide-gray-200'} divide-y`}>
                      {loading ? (
                        <tr>
                          <td className="px-4 py-8 text-center" colSpan={8}>
                            <div className="flex justify-center items-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                              <span className="ml-2">Loading students...</span>
                            </div>
                          </td>
                        </tr>
                      ) : students.data.length === 0 ? (
                        <tr>
                          <td className="px-4 py-8 text-center text-gray-500" colSpan={8}>
                            No students found
                          </td>
                        </tr>
                      ) : (
                        students.data.map((s, i) => (
                          <tr key={s.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">{i + 1}</td>
                            <td className="px-4 py-3 text-sm">{s.full_name}</td>
                            <td className="px-4 py-3 text-sm">{s.phone}</td>
                            <td className="px-4 py-3 text-sm">{s.email}</td>
                            <td className="px-4 py-3 text-sm">{s.school}</td>
                            <td className="px-4 py-3 text-sm">{s.school_level}</td>
                            <td className="px-4 py-3 text-sm">
                              {getPickupLabel(s.pickup_location)}
                            </td>
                            <td className="px-4 py-3 text-sm">{s.number_of_people}</td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${s.status === 'confirmed'
                                  ? 'bg-green-100 text-green-800'
                                  : s.status === 'contacted'
                                    ? 'bg-blue-100 text-blue-800'
                                    : s.status === 'cancelled'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}
                              >
                                {s.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {new Date(s.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))
                      )}
                      {/* ✅ Summary Row */}
                      <tr className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <td className="px-4 py-3 text-sm font-semibold" colSpan={6}>
                          Total Students: {students.data.length}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold">
                          Total Invitees:{' '}
                          {students.data.reduce((sum, s) => sum + (s.number_of_people || 0), 0)}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold">
                          Confirmed: {students.data.filter(s => s.status === 'confirmed').length}(
                          {students.data.length > 0
                            ? Math.round(
                              (students.data.filter(s => s.status === 'confirmed').length /
                                students.data.length) *
                              100
                            )
                            : 0}
                          %)
                        </td>
                        <td className="px-4 py-3 text-sm" colSpan={2}></td>
                      </tr>
                    </tbody>
                  </table>
                  {renderPagination(students.pagination, (p) =>
                    setStudents(prev => ({ ...prev, pagination: { ...prev.pagination, page: p } }))
                  )}
                </div>
              </div>
            )}

            {/* New Birth Tab */}
            {activeTab === 'newbirth' && (
              <div className="space-y-6">
                {/* New Birth Filters */}
                <div className="space-y-4">
                  <h4 className="font-semibold">Filters</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Date Range */}
                    <div className="col-span-1 md:col-span-2 lg:col-span-1">
                      <label className="text-sm font-medium block mb-2">Date Range</label>
                      <div className="flex gap-2">
                        <input
                          type="date"
                          value={newBirthFilters.startDate}
                          onChange={e => setNewBirthFilters(prev => ({ ...prev, startDate: e.target.value }))}
                          className={`border rounded px-3 py-2 flex-1 ${isDark
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300'
                            }`}
                        />
                        <input
                          type="date"
                          value={newBirthFilters.endDate}
                          onChange={e => setNewBirthFilters(prev => ({ ...prev, endDate: e.target.value }))}
                          className={`border rounded px-3 py-2 flex-1 ${isDark
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300'
                            }`}
                        />
                      </div>
                    </div>

                    {/* Decision */}
                    <div>
                      <label className="text-sm font-medium block mb-2">Decision</label>
                      <select
                        value={newBirthFilters.decision}
                        onChange={e => setNewBirthFilters(prev => ({ ...prev, decision: e.target.value }))}
                        className={`border rounded px-3 py-2 w-full ${isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300'
                          }`}
                      >
                        <option value="">All</option>
                        <option value="receive_jesus">Received Jesus</option>
                        <option value="rededicate">Rededicated</option>
                      </select>
                    </div>

                    {/* Residency Status */}
                    <div>
                      <label className="text-sm font-medium block mb-2">Residency Status</label>
                      <select
                        value={newBirthFilters.residency_status}
                        onChange={e => setNewBirthFilters(prev => ({ ...prev, residency_status: e.target.value }))}
                        className={`border rounded px-3 py-2 w-full ${isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300'
                          }`}
                      >
                        <option value="">All</option>
                        <option value="visiting">Visiting</option>
                        <option value="resident">Resident</option>
                        <option value="relocating">Relocating</option>
                      </select>
                    </div>

                    {/* Service Attended */}
                    <div>
                      <label className="text-sm font-medium block mb-2">Service Attended</label>
                      <select
                        value={newBirthFilters.service_attended}
                        onChange={e => setNewBirthFilters(prev => ({ ...prev, service_attended: e.target.value }))}
                        className={`border rounded px-3 py-2 w-full ${isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300'
                          }`}
                      >
                        <option value="">All</option>
                        <option value="first_service">First Service</option>
                        <option value="second_service">Second Service</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Export Button */}
                <div className="flex justify-end gap-2">
                  <button
                    // onClick={() => exportCSV(getDisplayedNewBirth(), 'new_birth_report.csv', visitorFilters)}
                    onClick={handleExportNewBirths}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                  >
                    Export CSV
                  </button>

                  <button
                    onClick={handleExportNewBirthsPDF}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Export PDF
                  </button>
                </div>

                {/* New Birth Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">SN</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Decision</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Phone</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Email</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Gender</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Service</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Residency</th>
                      </tr>
                    </thead>
                    <tbody className={`${isDark ? 'divide-gray-700' : 'divide-gray-200'} divide-y`}>
                      {loading ? (
                        <tr>
                          <td className="px-4 py-8 text-center" colSpan={9}>
                            <div className="flex justify-center items-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                              <span className="ml-2">Loading new birth records...</span>
                            </div>
                          </td>
                        </tr>
                      ) : newBirths.data.length === 0 ? (
                        <tr>
                          <td className="px-4 py-8 text-center text-gray-500" colSpan={9}>
                            No new birth records found
                          </td>
                        </tr>
                      ) : (
                        newBirths.data.map((n, i) => (
                          <tr key={n.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">{i + 1}</td>
                            <td className="px-4 py-3 text-sm">{new Date(n.response_date).toLocaleDateString()}</td>
                            <td className="px-4 py-3 text-sm capitalize">{n.decision?.replace('_', ' ')}</td>
                            <td className="px-4 py-3 text-sm">{n.title} {n.first_name} {n.surname}</td>
                            <td className="px-4 py-3 text-sm">{n.phone_main}</td>
                            <td className="px-4 py-3 text-sm">{n.email}</td>
                            <td className="px-4 py-3 text-sm capitalize">{n.gender}</td>
                            <td className="px-4 py-3 text-sm capitalize">{n.service_attended?.replace('_', ' ')}</td>
                            <td className="px-4 py-3 text-sm capitalize">{n.residency_status}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  {renderPagination(newBirths.pagination, (p) =>
                    setNewBirths(prev => ({ ...prev, pagination: { ...prev.pagination, page: p } }))
                  )}
                </div>
              </div>
            )}


            {/* Student Stats Tab */}
            {activeTab === 'student-stats' && (
              <div className="space-y-6">
                {/* Student Stats Filters */}
                <div className="space-y-4">
                  <h4 className="font-semibold">Filters</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Date Range */}
                    <div className="col-span-1 md:col-span-2 lg:col-span-1">
                      <label className="text-sm font-medium block mb-2">Date Range</label>
                      <div className="flex gap-2">
                        <input
                          type="date"
                          value={studentStatsFilters.startDate}
                          onChange={e => updateStudentStatsFilter('startDate', e.target.value)}
                          className={`border rounded px-3 py-2 flex-1 ${isDark
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300'
                            }`}
                        />
                        <input
                          type="date"
                          value={studentStatsFilters.endDate}
                          onChange={e => updateStudentStatsFilter('endDate', e.target.value)}
                          className={`border rounded px-3 py-2 flex-1 ${isDark
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300'
                            }`}
                        />
                      </div>
                    </div>

                    {/* Search */}
                    <div>
                      <label className="text-sm font-medium block mb-2">Search</label>
                      <input
                        value={studentStatsFilters.search}
                        onChange={e => updateStudentStatsFilter('search', e.target.value)}
                        placeholder="Name, phone, email..."
                        className={`border rounded px-3 py-2 w-full ${isDark
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300'
                          }`}
                      />
                    </div>

                    {/* Student Status */}
                    <div>
                      <label className="text-sm font-medium block mb-2">Status</label>
                      <select
                        value={studentStatsFilters.student_status}
                        onChange={e => updateStudentStatsFilter('student_status', e.target.value)}
                        className={`border rounded px-3 py-2 w-full ${isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300'
                          }`}
                      >
                        <option value="">All</option>
                        <option value="pending">Pending</option>
                        <option value="contacted">Contacted</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>

                    {/* School */}
                    <div>
                      <label className="text-sm font-medium block mb-2">School</label>
                      <select
                        value={studentStatsFilters.school}
                        onChange={e => updateStudentStatsFilter('school', e.target.value)}
                        className={`border rounded px-3 py-2 w-full ${isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300'
                          }`}
                      >
                        <option value="">All</option>
                        <option value="University of Abuja">University of Abuja</option>
                        <option value="Nasarawa State University">Nasarawa State University</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Export Button */}
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => exportCSV(getDisplayedStudentStats(), 'student_stats_report.csv', visitorFilters)}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                  >
                    Export CSV
                  </button>

                  <button
                    onClick={() => exportPDF(getDisplayedStudentStats(), 'student_stats_report.pdf', "StudentStats Report", visitorFilters)}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Export PDF
                  </button>
                </div>

                {/* Student Stats Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">SN</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">StudentName</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">School</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Student Count</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Percentage</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Total Invitees</th>
                      </tr>
                    </thead>
                    <tbody className={`${isDark ? 'divide-gray-700' : 'divide-gray-200'} divide-y`}>
                      {loading ? (
                        <tr>
                          <td className="px-4 py-8 text-center" colSpan={5}>
                            <div className="flex justify-center items-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                              <span className="ml-2">Loading student statistics...</span>
                            </div>
                          </td>
                        </tr>
                      ) : studentStats.length === 0 ? (
                        <tr>
                          <td className="px-4 py-8 text-center text-gray-500" colSpan={5}>
                            No student statistics found
                          </td>
                        </tr>
                      ) : (
                        studentStats.map((stat, i) => (
                          <tr key={i} className={`hover:bg-gray-50 ${isDark ? 'hover:bg-gray-700' : ''}`}>
                            <td className="px-4 py-3 text-sm">{1 + i}</td>
                            <td className="px-4 py-3 text-sm">{stat.full_name}</td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${stat.status === 'confirmed'
                                  ? 'bg-green-100 text-green-800'
                                  : stat.status === 'contacted'
                                    ? 'bg-blue-100 text-blue-800'
                                    : stat.status === 'cancelled'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}
                              >
                                {stat.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">{stat.school || 'All Schools'}</td>
                            <td className="px-4 py-3 text-sm font-semibold">{stat.student_count}</td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center">
                                <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                  <div
                                    className={`h-2 rounded-full ${stat.status === 'confirmed'
                                      ? 'bg-green-500'
                                      : stat.status === 'contacted'
                                        ? 'bg-blue-500'
                                        : stat.status === 'cancelled'
                                          ? 'bg-red-500'
                                          : 'bg-yellow-500'
                                      }`}
                                    style={{ width: `${stat.percentage || 0}%` }}
                                  ></div>
                                </div>
                                <span className="font-medium">{stat.percentage || 0}%</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold">{stat.total_invitees}</td>
                          </tr>
                        ))
                      )}
                      {/* Total Summary Row */}
                      {(studentStats || []).length > 0 && (
                        <tr className={`${isDark ? 'bg-blue-900' : 'bg-blue-50'} font-bold`}>
                          <td className="px-4 py-3 text-sm" colSpan={2}>
                            <strong>TOTAL</strong>
                          </td>
                          <td className="px-4 py-3 text-sm">

                          </td>
                          <td className="px-4 py-3 text-sm">

                          </td>
                          <td className="px-4 py-3 text-sm">
                            <strong>
                              {(studentStats || []).reduce((sum, stat) => sum + (stat.student_count || 0), 0)}
                            </strong>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <strong>100%</strong>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <strong>
                              {(studentStats || []).reduce((sum, stat) => sum + Number(stat.total_invitees || 0), 0)}
                            </strong>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {/* {renderPagination(studentStats.pagination, (p) =>
  setStudentStats(prev => ({ ...prev, pagination: { ...prev.pagination, page: p } }))
)} */}
                </div>

                {/* Summary Cards */}
                {(studentStats || []).length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="text-sm opacity-80 mb-2">Total Students</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {(studentStats || []).reduce((sum, stat) => sum + (stat.student_count || 0), 0)}
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="text-sm opacity-80 mb-2">Confirmed</p>
                      <p className="text-2xl font-bold text-green-600">
                        {(studentStats || [])
                          .filter(s => s.status === 'confirmed')
                          .reduce((sum, stat) => sum + (stat.student_count || 0), 0)}
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="text-sm opacity-80 mb-2">Contacted</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {(studentStats || [])
                          .filter(s => s.status === 'contacted')
                          .reduce((sum, stat) => sum + (stat.student_count || 0), 0)}
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="text-sm opacity-80 mb-2">Total Invitees</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {(studentStats || []).reduce((sum, stat) => sum + Number(stat.total_invitees || 0), 0)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && <div className="p-4 bg-red-100 text-red-800 rounded-lg">{error}</div>}
      </div>
    </Layout>
  );
};

export default Reports;
